import { forEach, isPlainObject } from 'remeda'
import { type DeferredEditor, deferEditor } from '../shared/deferred-editor'
import { COMPOSER_INSERT_MENTION_EVENT } from './types'

// Public types ----------------------------------------------------------------

export type ComposerHost = {
  readonly el: HTMLDivElement
  readonly pushEventTo: <Reply>(
    el: HTMLElement,
    event: string,
    payload: Record<string, unknown>,
    callback?: (reply: Reply) => void
  ) => void
}

// Constants -------------------------------------------------------------------

const editors = new WeakMap<ComposerHost, DeferredEditor>()
const events = [
  COMPOSER_INSERT_MENTION_EVENT,
  'phx:focus-input',
  'phx:clear-input'
] as const

// Public hook -----------------------------------------------------------------

export const QuillComposer = {
  // Retains targeted actions while the chat editor code is downloading.
  mounted(this: ComposerHost): void {
    const editor = deferEditor(this.el, async () => {
      const { mountEditor } = await import('./engine')

      return () => mountEditor(this)
    })
    const hostId = this.el.id
    const receive = (event: Event): void => {
      const payload: unknown =
        event instanceof CustomEvent ? event.detail : null
      if (!isPlainObject(payload)) {
        return
      }

      const target =
        event.type === COMPOSER_INSERT_MENTION_EVENT
          ? payload.editorId
          : payload.id
      if (target === hostId) {
        editor.receive({ name: event.type, payload })
      }
    }

    forEach(events, (name) => window.addEventListener(name, receive))
    editors.set(this, {
      ...editor,
      destroy: () => {
        forEach(events, (name) => window.removeEventListener(name, receive))
        editor.destroy()
      }
    })
  },

  // Keeps updates with the editor that owns the current message.
  updated(this: ComposerHost): void {
    editors.get(this)?.updated()
  },

  // Prevents delayed imports and focus requests from reviving a closed chat.
  destroyed(this: ComposerHost): void {
    editors.get(this)?.destroy()
    editors.delete(this)
  }
}
